import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

export default function BatchDeleteStudentPathwayEnrollmentDialog({
  open,
  onOpenChange,
  enrolments,
  onDeleted,
}) {
  const { getToken } = useAuth();
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeleting(false);
  }, [open]);

  const selected = useMemo(() => {
    const rows = Array.isArray(enrolments) ? enrolments : [];
    return rows.filter(Boolean);
  }, [enrolments]);

  const userIds = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const e of selected) {
      const id = e?.user_id ?? e?.userId;
      if (!id) continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }, [selected]);

  const pathwayIds = useMemo(() => {
    const ids = new Set();
    selected.forEach((e) => {
      const id = e?.pathway_id ?? e?.pathwayId;
      if (id === 0 || id) ids.add(Number(id));
    });
    return Array.from(ids).filter((n) => Number.isFinite(n));
  }, [selected]);

  const pathwayId = pathwayIds.length === 1 ? pathwayIds[0] : null;
  const hasMixedPathways = pathwayIds.length > 1;

  const pathwayName = useMemo(() => {
    return selected?.[0]?.pathway || "this pathway";
  }, [selected]);

  const selectedCount = selected.length;
  const canSubmit =
    !deleting &&
    selectedCount >= 2 &&
    userIds.length >= 2 &&
    pathwayId != null &&
    !hasMixedPathways;

  const handleDelete = async () => {
    if (selectedCount < 2) {
      toast.error("Select at least 2 enrolments.");
      return;
    }
    if (userIds.length === 0) {
      toast.error("Missing user ids for selected enrolments.");
      return;
    }
    if (hasMixedPathways || pathwayId == null) {
      toast.error(
        "Bulk delete requires enrolments from the same pathway (pathway_id)."
      );
      return;
    }

    setDeleting(true);
    try {
      const token = await getToken();
      if (!token) return;

      const payload = {
        pathway_id: Number(pathwayId),
        user_ids: userIds,
      };

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      };

      const res = await axios.delete(
        api_endpoint + "/pathway/enrollment/bulk-delete",
        reqConfig
      );

      const data = res?.data;
      const totalDeleted =
        data?.total_deleted != null ? Number(data.total_deleted) : null;
      const totalErrors =
        data?.total_errors != null ? Number(data.total_errors) : null;

      const deletedCount = Number.isFinite(totalDeleted)
        ? totalDeleted
        : Array.isArray(data?.deleted_user_ids)
          ? data.deleted_user_ids.length
          : 0;

      if (Number.isFinite(totalErrors) && totalErrors > 0) {
        toast.success(
          `Deleted ${deletedCount} enrolment${deletedCount === 1 ? "" : "s"}. ${totalErrors} failed.`
        );
      } else {
        toast.success(
          `Deleted ${deletedCount} enrolment${deletedCount === 1 ? "" : "s"} successfully.`
        );
      }

      onOpenChange?.(false);
      onDeleted?.(data);
    } catch (error) {
      console.error("Failed to bulk delete pathway enrolments", error);
      toast.error("Failed to delete enrolments. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const isDisabled = deleting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-(--surface-panel) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Delete Pathway Enrolments
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-800)">
            Delete <b>{selectedCount}</b> enrolment{selectedCount === 1 ? "" : "s"} from <b>{pathwayName}</b>. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {hasMixedPathways ? (
          <div className="rounded-md border border-(--color-destructive) bg-(--surface-base) p-3 text-sm text-(--neutral-800)">
            Bulk delete requires all selected enrolments to be from the same pathway.
          </div>
        ) : null}

        <DialogFooter className="mt-4 flex gap-2 justify-between">
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isDisabled}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant=""
            onClick={handleDelete}
            disabled={isDisabled || !canSubmit}
          >
            {deleting ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
